import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { Figure } from '@/pages/ibama/OverviewPage';
import { fetchIbamaFees } from '@/services/ibama';

/**
 * The TCFA: a levy every registered activity pays, never a sanction.
 *
 * A ledger of GRUs with no payer -- no CNPJ, no name -- so it can say how
 * much the fee raised per year and per state and cannot say who paid it.
 * The fence is on its face, the per-company exposure is refused, and
 * nothing here is pooled with an auto de infracao.
 */
export function IbamaFeesPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'fees'],
    queryFn: fetchIbamaFees,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const ratio = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:fees.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:fees.subtitle')}</p>
      </header>

      <p className="rounded-md border border-dashed p-3 text-xs text-foreground" role="note">
        {t('ibama:fees.fence')}
      </p>

      <ExplainerCard i18nKey="ibama:fees.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ibama/export/fees" label={t('ibama:fees.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-ledger>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:fees.ledger.title')}
          </h2>
          <Answer answer={data.ledger} help={t('ibama:fees.ledger.help')}>
            {(l) => (
              <div className="grid grid-cols-2 gap-3">
                <Figure label={t('ibama:fees.ledger.rows')} value={number.format(l.rows)} />
                <Figure
                  label={t('ibama:fees.ledger.span')}
                  value={`${l.first_year ?? '—'} – ${l.last_year ?? '—'} (${number.format(
                    l.states,
                  )} UF)`}
                />
                <Figure
                  label={t('ibama:fees.ledger.charged')}
                  value={currency.format(l.charged_total)}
                />
                <Figure
                  label={t('ibama:fees.ledger.paid')}
                  value={`${currency.format(l.paid_total)} (${ratio.format(l.paid_over_charged)})`}
                />
              </div>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="ibama:fees.guide" />
          </div>
        </section>

        <RefusalCard title={t('ibama:fees.exposure.title')} answer={data.exposure} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-by-year>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:fees.years.title')}
          </h2>
          <Answer answer={data.by_year} help={t('ibama:fees.years.help')}>
            {(rows) => (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('ibama:fees.years.year')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:fees.years.rows')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:fees.years.charged')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:fees.years.paid')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:fees.years.ratio')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((y) => (
                      <tr key={y.year} className="border-t" data-year={y.year}>
                        <td className="px-3 py-2 tabular-nums text-foreground">{y.year}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(y.rows)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {currency.format(y.charged)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {currency.format(y.paid)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {ratio.format(y.paid_over_charged)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-by-state>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:fees.states.title')}
          </h2>
          <Answer answer={data.by_state} help={t('ibama:fees.states.help')}>
            {(rows) => (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('ibama:fees.states.uf')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:fees.states.rows')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:fees.states.charged')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ibama:fees.states.paid')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((u) => (
                      <tr key={u.uf} className="border-t" data-uf={u.uf}>
                        <td className="px-3 py-2 text-foreground">{u.uf}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(u.rows)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {currency.format(u.charged)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {currency.format(u.paid)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Answer>
        </section>
      </div>
    </div>
  );
}
